import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import { Button } from "../../atoms";
import { SvgIconHandler } from "../../atoms/SvgIconHandler";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShare } from "@quintype/components";
import { SocialShareTemplate } from "../../../arrow";
import { useDeviceType } from "../../customHooks/useDeviceType";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { LISTING_PAGE_RANKING_DESCRIPTIONS } from "../../../../../config/entity-ranking-descriptions";
import { trackLoadMoreClick } from "../../utils";

import "./top15-rankings.m.css";

const Top15Rankings = ({ data = [] }) => {
  const { ADVERTISEMENT, EXPLORE_LATEST_LIST, EXPLORE_FULL_LIST, SNAPSHOT, LOAD_MORE } = STATIC_KEYWORDS;
  const { top15ForEveryYear, top15PageTitle, top15PageDescription, top15Label = "" } = data;
  const isMobile = useDeviceType() !== "desktop";
  let isTracking = false;
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";

  const pageType = get(qtState, ["pageType"], {});
  const sketchesHost = get(qtState, ["config", "sketches-host"], {});
  const categoryMap = {
    "Most Powerful Women": "people",
    "40 Under 40": "people",
    "Best B-Schools India": "schools",
    "Top 100 Billionaires": "people",
  };

  // Generic tracking function for all rankings interactions
  const trackRankingInteraction = (action, value, linkUrl) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_ranking_list_click",
        event_action: action,
        link_text: value?.toLowerCase(),
        link_url: linkUrl,
        position: 2,
        category: category,
        sub_category: subCategory,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const trackItemClick = (name, position, rankingName, year) => {
    const category = categoryMap[top15Label] || "companies";
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_ranking_list_click",
        link_text: name.toLowerCase(),
        link_position: position,
        link_category: `${rankingName} ${year}`.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Track "Explore Full List" clicks
  const trackSnapshotClick = (rankingName, year, position, linkText = "explore full list") => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_ranking_list_click",
        link_text: linkText.toLowerCase(),
        link_position: position,
        link_category: `${rankingName} ${year}`.toLowerCase(),
        category: category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Track "Explore Latest List" button click
  const trackExploreLatestListClick = (linkText = "", linkUrl = "") => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType);
      window.dataLayer.push({
        event: "dl_cta_click",
        link_text: linkText,
        link_url: `${sketchesHost}${linkUrl}`,
        link_position: 1,
        link_category: top15PageTitle?.toLowerCase(),
        category: category,
        sub_category: subCategory,
        contentId: contentId,
        template_name: getTemplateName(pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const description = LISTING_PAGE_RANKING_DESCRIPTIONS[top15PageTitle];
  const logoName = {
    "fortune-500": isMobile ? "/images/banners/Fortune500Mobile.png" : "/images/banners/Fortune500Desktop.png",
    "the-next-500": isMobile ? "/images/banners/Next500Mobile.png" : "/images/banners/Next500Desktop.png",
    "the-emerging-companies": isMobile ? "/images/banners/EmergingCompaniesMobile.png" : "/images/banners/EmergingCompaniesDesktop.png",
    "40under40": isMobile ? "/images/banners/40under40Mobile.png" : "/images/banners/40under40Desktop.png",
    "most-powerful-women": isMobile ? "/images/banners/MpwMobile.png" : "/images/banners/MpwDesktop.png",
    "best-b-schools": isMobile ? "/images/banners/BschoolMobile.png" : "/images/banners/BschoolDesktop.png",
    "mnc-500": isMobile ? "/images/banners/MNC500Mobile.png" : "/images/banners/MNC500Desktop.png",
    "indias-top-billionaires": isMobile ? "/images/banners/billionaires-mobile.png" : "/images/banners/billionaires-desktop.png",
  };

  const limit = 3;
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [shareURL, setShareURL] = useState("");
  const { slug } = data;

  const [sliceLimit, setSliceLimit] = useState(15);

  const companyList = slug === "fortune-500" || slug === "the-next-500" ||  slug === "the-emerging-companies";
  const bulletPoints = slug === "40under40";
  const customContainer =
    slug === "best-b-schools" ? "best-b-schools-container snapshot-container" : "snapshot-container";

  useEffect(() => {
    setShareURL(window.location.href);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_page_info",
      cms_author_name: "NA",
      byline: "NA",
      category: "rankings",
      sub_category: slug.replace(/-/g, " "),
      content_id: "NA",
      template_name: "listing",
      publish_date: "NA",
      publish_time: "NA",
      login_status: "not-logged-in", // will be changed
      subscription_status: "not-subscribed", // will be changed
      ad_free: false, // will be changed
      login_wall_enabled: false,
      platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
    });
  }, []);

  useEffect(() => {
    isMobile ? setSliceLimit(5) : setSliceLimit(15);
  }, [isMobile]);

  const snapshotContainer = (yearObj, top15, label, displayCompanyNames) => {
    return (
      <div styleName={customContainer}>
        <hr styleName="horizontal-rule-big" />
        <div styleName="label-heading-container">
          <a
            href={`/rankings/${slug}/${yearObj.name}`}
            styleName="heading-text"
            onMouseDown={() => trackSnapshotClick(top15Label, yearObj.name, -2, `${label} ${yearObj.name} ${SNAPSHOT}`)}
          >
            {label} <span styleName="year-text">{yearObj.name}</span> {SNAPSHOT}
          </a>
          <a
            href={`/rankings/${slug}/${yearObj.name}`}
            styleName="full-list-container"
            onMouseDown={() => trackSnapshotClick(top15Label, yearObj.name, -1)}
          >
            <span styleName="full-list-text">{EXPLORE_FULL_LIST}</span>
            <span styleName="svg-flipped">
              <SvgIconHandler type="arrow" width="24px" height="24px" viewBox="0 0 24 24" />
            </span>
          </a>
        </div>
        <div styleName="list-items">
          {top15?.slice(0, sliceLimit).map((item, index) => {
            const { id, slug: itemSlug = undefined, name, company } = item;
            return (
              <a
                key={`list-item-${id}`}
                styleName={`${slug === "best-b-schools" || "indias-top-billionaires" || !itemSlug ? "item-no-link item" : "item"}`}
                href={
                  slug === "best-b-schools" || "indias-top-billionaires" || !itemSlug
                    ? "javascript:;"
                    : `${companyList ? "/companies/" : "/people/"}${itemSlug}`
                }
                style={{ "--index": index }}
                onMouseDown={() => {
                  trackItemClick(displayCompanyNames?.[index] || name || company, index + 1, top15Label, yearObj.name);
                }}
              >
                {!bulletPoints ? <span styleName="numbers">{index + 1}</span> : <span styleName="bullets" />}
                <span styleName="name">{displayCompanyNames?.[index] || name || company}</span>
              </a>
            );
          })}
          <hr styleName="horizontal-rule-thin-small dark-seperator" style={{ gridColumn: "2" }} />
          <hr styleName="horizontal-rule-thin-small dark-seperator" style={{ gridColumn: "4" }} />
        </div>
      </div>
    );
  };

  return (
    <>
      <div styleName="bg-image" style={{ backgroundImage: `url("${logoName[data.slug]}")` }} />
      <div styleName="container">
        <div styleName={`header-container ${isAdfreePage ? "no-ad-header-container" : ""}`}>
          <div styleName="main-container">
            <h1 styleName="headline">{top15PageTitle}</h1>
            <div styleName="description">{description}</div>
            <div styleName="button-container">
              <a
                href={`/rankings/${slug}/${top15ForEveryYear[0].year.name}`}
                onMouseDown={() =>
                  trackExploreLatestListClick(
                    "explore_latest_list",
                    `/rankings/${slug}/${top15ForEveryYear?.[0]?.year?.name}`
                  )
                }
              >
                <Button customStyleName="bg-brand-primary">{EXPLORE_LATEST_LIST}</Button>
              </a>
              <span className="content-style">
                <SocialShare
                  popupText="Share this URL"
                  template={(props) =>
                    SocialShareTemplate({
                      ...props,
                      onShareClick: (platform) =>
                        trackRankingInteraction("share_click", platform?.toLowerCase() || "share", shareURL),
                    })
                  }
                  fullUrl={encodeURI(shareURL)}
                  title={top15Label}
                  shareIconId={`share-icon`}
                />
              </span>
            </div>
          </div>
          {!isAdfreePage && (
            <>
              <hr styleName="horizontal-rule-thin dark-seperator" />
              <div styleName="ad-container">
                <ListingAdSlot
                  adType="right_side"
                  pageType="top15-rankings"
                  adStyleName="ad-slot-size-300x250"
                  adSlotConfigName="mRec_ad"
                  index={1}
                />
              </div>
            </>
          )}
        </div>
        {top15ForEveryYear.slice(0, currentLimit).map((entity) => {
          const { year, top15, displayNames } = entity;
          const displayCompanyNames = displayNames?.[0]?.["company-display-name"];

          return snapshotContainer(year, top15, top15Label, displayCompanyNames);
        })}
        {currentLimit < top15ForEveryYear.length && (
          <div styleName="load-more-container">
            <button
              styleName="load-more-button"
              onClick={() => {
                setCurrentLimit((prevLimit) => prevLimit + limit);
                trackLoadMoreClick({
                  linkCategory: slug,
                  category: "ranking",
                  subCategory: slug,
                  templateName: "listing",
                });
              }}
            >
              {LOAD_MORE}
            </button>
          </div>
        )}
        {/* {!isAdfreePage && (
          <ListingAdSlot
            adType="above_footer"
            pageType="top15-rankings"
            adStyleName="ad-slot-size-728x90-300x250"
            index={3}
          />
        )} */}
      </div>
    </>
  );
};

Top15Rankings.propTypes = {
  data: PropTypes.array,
};

export { Top15Rankings };
